// RowSelectionContext.js
import React, {createContext, useContext, useMemo, useRef, useEffect, useState} from "react";
import { createRowSelectionHandler } from "@portfolio/utils/selectionUtils";
import {createGlobalKeyHandler} from "@portfolio/utils/createGlobalKeyHandler";

export const RowSelectionContext = createContext(null);

export const useRowSelectionContext = () => useContext(RowSelectionContext);

export const RowSelectionProvider = ({
                                         children,
                                         stocks,
                                         lastSelectedSymbolRef,
                                         rowHeightGridLayout,
                                     }) => {
    const visibleRangeRef = useRef({ start: 0, stop: 0 });
    const pageScrollPendingFrames = useRef(0);
    const scrollDirectionRef = useRef(null);
    const targetScrollIndexRef = useRef(null);
    const rowVirtualizerRef = useRef(null);

    const listRef = useRef(null);
    const outerListRef = useRef(null);
    const inputRef = useRef(null);
    const [selectedRowKey, setSelectedRowKey] = useState(null);
// Inside RowSelectionProvider
    const subscribers = useRef(new Set());
    const selectedRowKeyRef = useRef(null);
    const selectedRowIndexRef = useRef(null);
    const rowRerenderTriggers = useRef(new Map());
    const arrowNavigationActiveRef = useRef(false);
    const arrowKeyPendingRef = useRef(false); // ✅ new

    const notify = () => {
        for (const cb of subscribers.current) cb();
    };

    const subscribe = (callback) => {
        subscribers.current.add(callback);
        return () => subscribers.current.delete(callback);
    };

    const getSnapshot = () => selectedRowKey;

    const updateSelectedRowIndex = useMemo(() =>
            createRowSelectionHandler({
                selectedRowKeyRef,
                selectedRowIndexRef,
                rowRerenderTriggers
            }),
        [stocks, rowRerenderTriggers, lastSelectedSymbolRef]
    );

    useEffect(() => {
        const handler = createGlobalKeyHandler({
            rowVirtualizer: rowVirtualizerRef.current,
            stocks,
            updateSelectedRowIndex,
            selectedRowKeyRef,
            selectedRowIndexRef,
            visibleRangeRef,
            pageScrollPendingFrames,
            scrollDirectionRef,
            targetScrollIndexRef,
            arrowNavigationActiveRef,
            arrowKeyPendingRef
        });
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [stocks]);

    return (
        <RowSelectionContext.Provider
            value={{
                selectedRowKeyRef,
                selectedRowIndexRef,
                rowRerenderTriggers,
                updateSelectedRowIndex,
                visibleRangeRef,
                pageScrollPendingFrames,
                listRef,
                outerListRef,
                inputRef,
                scrollDirectionRef,
                targetScrollIndexRef,
                rowVirtualizerRef,
                selectedRowKey,
                subscribe,
                getSnapshot,
                arrowNavigationActiveRef,
                arrowKeyPendingRef,
                rowHeightGridLayout
            }}
        >
            {children}
        </RowSelectionContext.Provider>
    );
};
